"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { commonTranslations, homeTranslations, ecosystemPageTranslations, newsPageTranslations, careersPageTranslations } from "../translations";

type Language = "vi" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (keyPath: string) => any;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const allTranslations = {
  common: commonTranslations,
  home: homeTranslations,
  ecosystemPage: ecosystemPageTranslations,
  newsPage: newsPageTranslations,
  careersPage: careersPageTranslations,
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>("vi");

  useEffect(() => {
    const savedLang = localStorage.getItem("language") as Language;
    if (savedLang && (savedLang === "vi" || savedLang === "en")) {
      setLanguage(savedLang);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const t = (keyPath: string) => {
    const keys = keyPath.split(".");
    if (keys.length < 1) return keyPath;

    const namespace = keys[0];
    const remainingKeys = keys.slice(1);

    let current: any = allTranslations;

    // First, go into the namespace (common, home, etc.)
    if (current[namespace] !== undefined) {
      current = current[namespace];
    } else {
      return keyPath;
    }

    // Then, go into the specific language (vi, en)
    if (current[language] !== undefined) {
      current = current[language];
    } else {
      return keyPath;
    }

    // Finally, navigate the remaining keys
    for (const key of remainingKeys) {
      if (current[key] !== undefined) {
        current = current[key];
      } else {
        return keyPath;
      }
    }

    return current;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
